<template>
    <v-card>
        <v-card-title>
            <v-row>
                <v-col>
                    {{ dialogTitle }}
                </v-col>
                <v-col class="text-right">
                    <v-btn
                        v-if="isAuthorized('Auth:Manage') && !uploadAdHocMeasurementsFormVisible"
                        @click="uploadAdHocMeasurementsFormVisible = true"
                        >Upload Ad-Hoc Measurements</v-btn
                    >
                    <v-btn
                        v-if="uploadAdHocMeasurementsFormVisible"
                        @click="uploadAdHocMeasurementsFormVisible = false"
                        >Upload Task Measurements</v-btn
                    >
                </v-col>
            </v-row>
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="4" v-if="uploadAdHocMeasurementsFormVisible">
                    <v-autocomplete
                        clearable
                        v-model="selectedThingSchemaId"
                        :items="thingSchemaOptions"
                    />
                    <v-btn
                        v-if="selectedThingSchemaId"
                        @click="downloadAdHocUserMeasurementSetTemplate"
                    >
                        Download Template
                    </v-btn>
                </v-col>
                <v-col v-if="collectUserDate" cols="4" sm="6">
                    <v-text-field
                        :type="dateFieldType"
                        v-model="editCollectionDateTimeISOString"
                        :label="collectionDateTimeLabel"
                    />
                </v-col>
                <v-col cols="4" sm="6">
                    <v-file-input
                        label="User Measurements Excel File"
                        accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        :rules="[requiredFile]"
                        @change="updateUserMeasurementFile"
                    />
                </v-col>
            </v-row>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="cancel"> Cancel </v-btn>
            <v-btn color="primary" @click="save"> Save </v-btn>
        </v-card-actions>
    </v-card>
</template>
<script>
import { defineComponent } from "vue";

import { withLoadingScreen } from "@/lib/loading";
import { mapGetters } from "vuex";
import { measurementsAPI } from "@/services/api.services";
import { toDateTimeForExperiment } from "@/lib/dateUtils";
import { buildCollectionDateTimeFieldConfig } from "@/lib/measurementsUtils";
import _ from "lodash";
import { buildSchemaSelectFormField } from "@/lib/schemaUtils";
import { getThingSchemaForId } from "@/lib/thingUtils";
import { downloadFile } from "@/lib/downloadFileUtil";
import { requiredFile } from "@/lib/validationUtils";

export default defineComponent({
    props: {
        experiment: {
            type: Object,
            required: true,
        },
        protocol: {
            type: Object,
            required: true,
        },
        dataCollectionTask: {
            type: Object,
            required: false,
        },
        readoutGroup: {
            type: Object,
            required: false,
        },
        taskDisplayName: {
            type: String,
            required: false,
        },
        userId: {
            type: String,
            required: true,
        },
        collectionDateTime: {
            type: Object,
            required: false,
        },
        collectUserDate: {
            type: Boolean,
            required: true,
        },
    },

    data() {
        return {
            excelFile: null,
            dateFieldType: "datetime-local",
            collectionDateTimeLabel: "",
            editCollectionDateTimeISOString: "",
            thingSchemaField: null,
            thingSchemaOptions: [],
            selectedThingSchemaId: null,
            uploadAdHocMeasurementsFormVisible: false,
        };
    },

    computed: {
        ...mapGetters({
            user: "getAuthUser",
            isAuthorized: "isAuthorized",
        }),
        dialogTitle() {
            if (this.taskDisplayName) {
                return `Import Measurements for ${this.userId} - ${this.taskDisplayName}`;
            } else if (this.uploadAdHocMeasurementsFormVisible) {
                if (this.selectedThingSchema) {
                    return `Import Ad Hoc ${this.selectedThingSchema.displayName} Measurements for ${this.userId}`;
                } else {
                    return `Import Ad Hoc Measurements for ${this.userId}`;
                }
            } else {
                return `Import Measurements for ${this.userId}`;
            }
        },
        selectedThingSchema() {
            return getThingSchemaForId(this.protocol, this.selectedThingSchemaId);
        },
    },

    mounted() {
        if (this.collectUserDate) {
            const { dateFieldType, editCollectionDateTimeISOString, collectionDateTimeLabel } =
                buildCollectionDateTimeFieldConfig(
                    this.collectionDateTime,
                    this.readoutGroup,
                    this.experiment
                );
            this.dateFieldType = dateFieldType;
            this.editCollectionDateTimeISOString = editCollectionDateTimeISOString;
            this.collectionDateTimeLabel = collectionDateTimeLabel;
        }

        this.thingSchemaField = buildSchemaSelectFormField(
            this.protocol,
            null,
            (selectedThingSchema) => {
                this.selectedThingSchema = selectedThingSchema;
            }
        );

        this.thingSchemaOptions = this.thingSchemaField.options;
    },

    methods: {
        requiredFile,
        async downloadAdHocUserMeasurementSetTemplate() {
            await withLoadingScreen(async () => {
                const response = await measurementsAPI.downloadAdHocUserMeasurementsTemplate(
                    this.experiment.code,
                    this.selectedThingSchemaId
                );
                const filename = `${this.experiment.code}-${this.selectedThingSchema.displayName} Measurements.xlsx`;
                downloadFile(filename, response);
            });
        },
        updateUserMeasurementFile(event) {
            this.excelFile = event.target.files[0];
        },
        cancel() {
            this.$emit("closed");
        },
        save() {
            withLoadingScreen(async () => {
                try {
                    const commonImportRequestBody = this.buildCommonImportRequestBody();

                    if (this.uploadAdHocMeasurementsFormVisible && this.selectedThingSchemaId) {
                        const thingSchemaId = this.selectedThingSchemaId;
                        const userMeasurementImportResult =
                            await measurementsAPI.importAdHocUserMeasurements({
                                ...commonImportRequestBody,
                                thingSchemaId,
                            });
                        this.$emit("saved", userMeasurementImportResult);
                    } else {
                        const dataCollectionTaskId = this.dataCollectionTask
                            ? this.dataCollectionTask.id
                            : null;
                        const userMeasurementImportResult =
                            await measurementsAPI.importTaskUserMeasurements({
                                ...commonImportRequestBody,
                                dataCollectionTaskId,
                            });
                        this.$emit("saved", userMeasurementImportResult);
                    }
                } catch (err) {
                    const msg = _.get(err, "response.data.error") || err.message;
                    this.$emit("error", msg);
                }
            });
        },
        buildCommonImportRequestBody() {
            const collectionDateTime = this.collectUserDate
                ? toDateTimeForExperiment(this.editCollectionDateTimeISOString, this.experiment)
                : null;

            return {
                experimentCode: this.experiment.code,
                userId: this.userId,
                collectionDateTime: collectionDateTime ? collectionDateTime : null,
                file: this.excelFile,
            };
        },
    },
});
</script>
